/**
 * structure-effects.ts — v0M9.13 (2026-07-24).
 *
 * Runtime interpreters for the free-text `effects` string on each
 * StructureSpec. Mirrors `knowledge-effects.ts` but for the structure
 * roster instead of the knowledge tree.
 *
 * Structure effects are scoped to the settlement / province that owns
 * the structure — not faction-wide like knowledge. So helpers here take
 * a `province` (or `settlement`) argument rather than a `faction`.
 *
 * Currently wired:
 *   - Garrison Maximum +N (Garrison Quarters).
 *   - Armor +N (Armorers Guild — applied at cohort recruit-spawn time).
 *   - Settlement gains Walls (Settlement Walls — v0M10.7; consulted by
 *     battle-factory.ts::settlementHasWallsStructure to route sieges to
 *     the walled vs unwalled battlefield template).
 *
 * Not yet wired:
 *   - Commander XP +N per day (Officer Academy — needs tick hook + ZoC check).
 *   - Unlocks Cohort Recruitment (Barracks, Archery Yard, Siege Foundary —
 *     narrative label only; enforcement lives in the cohort structure_prereq).
 *   - Enables Supply Wagons / Engineers / Spies attachments (Merchant
 *     Guild, Engineering Guild, Thieves Guild — need attachment systems).
 */

import type { Province } from './state';

/** Matches "Garrison Maximum + 2" style effect strings. */
const GARRISON_MAX_BONUS_RE = /Garrison\s+Maximum\s*\+\s*(\d+)/i;

/** Matches "Armor + 5" style effect strings. Case-insensitive. */
const ARMOR_BONUS_RE = /Armor\s*\+\s*(\d+)/i;

/** Matches "Commander XP + 5 per day" style effect strings. Used by
 *  Officer Academy at v1. Case-insensitive. */
const COMMANDER_XP_PER_DAY_RE = /Commander\s+XP\s*\+\s*(\d+)\s+per\s+day/i;

/**
 * v0M10.5 (2026-07-24) — Officer Academy caps at commander level 5 per
 * Michael's description string. Hardcoded here rather than parsed from
 * the description because "only to level 5" is a bespoke phrasing; if
 * more level-capped XP structures land, this generalizes to a regex.
 */
export const OFFICER_ACADEMY_LEVEL_CAP = 5;

/**
 * v0M10.1 (2026-07-24) — Static map from attachment type → the structure
 * that must be built in a settlement (whose ZoC the army sits in) to
 * unlock that attachment. Hardcoded because Herbalist Guild's effect
 * string in the xlsx is currently blank, so effect-string parsing
 * would miss it. If Michael later adds new attachment types, extend
 * this map + the ATTACHMENT_TYPES tuple in state.ts.
 */
export const ATTACHMENT_UNLOCK_STRUCTURE: Readonly<Record<string, string>> = {
  'Engineer': 'Engineering Guild',
  'Healer': 'Herbalist Guild',
  'Supply Wagon': 'Merchant Guild',
};

/**
 * Sum of "Garrison Maximum +N" bonuses across the province's built
 * structures. Consumers add this to the base `garrison_max_per_settlement`
 * dial to compute the effective per-settlement garrison cap.
 *
 * Additive across duplicate structures (though at v1 the sim only
 * permits one copy per structure per settlement, so N is effectively
 * 0 or +N for a single Garrison Quarters).
 */
export function garrisonMaxBonus(
  province: Province,
  structures: Record<string, { effects: string }>,
): number {
  let bonus = 0;
  for (const s of province.structures) {
    const spec = structures[s.name];
    if (!spec) continue;
    const m = GARRISON_MAX_BONUS_RE.exec(spec.effects);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) bonus += n;
    }
  }
  return bonus;
}

/**
 * Sum of "Armor +N" bonuses granted by structures in the recruit-
 * completing province. Applied to a cohort at spawn time so the boost
 * is a *baked-in* stat of the cohort — cohorts recruited before the
 * Armorers Guild is built retain their old armor value.
 *
 * Consumer: `economy.ts::spawnCohort`. Reads province.structures and
 * multiplies the cohort's spec.armor by 1 + this delta (integer bonus).
 */
export function recruitArmorBonus(
  province: Province,
  structures: Record<string, { effects: string }>,
): number {
  let bonus = 0;
  for (const s of province.structures) {
    const spec = structures[s.name];
    if (!spec) continue;
    const m = ARMOR_BONUS_RE.exec(spec.effects);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) bonus += n;
    }
  }
  return bonus;
}

/**
 * v0M10.5 (2026-07-24) — Commander-XP-per-day bonus from a province's
 * built structures. Used by Officer Academy at v1 ("Commander XP + 5
 * per day, only to level 5").
 *
 * Callers apply the bonus only when (a) the commander's army sits in
 * this province's settlement ZoC, AND (b) the commander's current
 * level is BELOW `OFFICER_ACADEMY_LEVEL_CAP`. Both checks live at the
 * economy.ts consumer site so this helper stays pure — just parses
 * the daily-XP delta from all matching structures in the province.
 */
export function commanderXpPerDayBonus(
  province: Province,
  structures: Record<string, { effects: string }>,
): number {
  let bonus = 0;
  for (const s of province.structures) {
    const spec = structures[s.name];
    if (!spec) continue;
    const m = COMMANDER_XP_PER_DAY_RE.exec(spec.effects);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) bonus += n;
    }
  }
  return bonus;
}
